/**
 * Cockpit OPERATE view — the RESIDENT side panel (Cockpit Phase 5, §3).
 *
 * The resident copilot that lives alongside a published cockpit. Three lanes:
 *
 *   1. CHIPS (proactive)   — ≤3 server-curated suggestions at the top. Each is
 *                            dismissable (sticky-per-kind via the dismiss
 *                            endpoint). Clicking a chip ONLY pre-fills the
 *                            composer (or anchors a row / opens an approval) —
 *                            it NEVER auto-acts. This is the "no-auth-on-click"
 *                            invariant from COCKPIT-PLAN §3.
 *   2. CHAT (reactive)     — a thread + composer that POSTs `/resident/chat`.
 *                            The answer renders as a markdown bubble. Any
 *                            PROPOSED writes come back as `proposedActions`
 *                            (pending cockpit_action approvals) and surface an
 *                            inline ApprovalCard — the resident only PROPOSES,
 *                            the user (or a different approver) approves.
 *   3. (queued workflows)  — informational note when the resident kicked off a
 *                            workflow that needs no approval.
 *
 * The resident chat STREAMS over SSE (Cockpit Phase 5, Rule #24): the resident
 * assembles the 6-layer cockpit context server-side, then streams the answer
 * token-by-token (typing effect) and finalizes with any proposed actions +
 * queued workflows. We reuse the visual grammar of `ArgusLiveChatPanel` (Argus
 * avatar bubbles, MarkdownRenderer, the same composer affordances) for parity,
 * and now its streaming UX too — tokens render as they arrive.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, RotateCcw, Sparkles, X, Bell, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArgusEye } from '@/components/icons';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { ApprovalCard } from '@/components/chat/approval-card';
import { cn } from '@/lib/utils';
import {
  useArgusLiveResidentChips,
  useArgusLiveResidentChat,
  useDismissArgusLiveResidentChip,
  type ResidentChip,
  type ResidentProposedAction,
} from '@/lib/use-argus-live';

// ─── Local message shape ────────────────────────────────────────────────────

interface ResidentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Proposed (approval-gated) writes attached to an assistant turn. */
  proposedActions?: ResidentProposedAction[];
  timestamp: number;
}

let _residentMsgCounter = 0;
function makeResidentMsgId(): string {
  return `cr-msg-${++_residentMsgCounter}-${Date.now()}`;
}

const RESIDENT_WELCOME = `
Hi, I'm your cockpit's resident. Ask me about anything on this dashboard — I can
read its live data, explain a metric, or **propose** an action for you to approve.

I never run a write on my own; anything that changes data comes back as an
approval card you confirm.
`.trim();

// ─── Props ──────────────────────────────────────────────────────────────────

export interface CockpitResidentPanelProps {
  /** Dashboard (cockpit) id — scopes every resident call. */
  dashboardId: string;
  /** A stable session id for the resident thread (memory continuity). */
  sessionId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CockpitResidentPanel({
  dashboardId,
  sessionId,
}: CockpitResidentPanelProps) {
  const [messages, setMessages] = useState<ResidentMessage[]>(() => [
    {
      id: makeResidentMsgId(),
      role: 'assistant',
      content: RESIDENT_WELCOME,
      timestamp: Date.now(),
    },
  ]);
  const [input, setInput] = useState('');
  const tailRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const chipsQuery = useArgusLiveResidentChips(dashboardId);
  const residentChat = useArgusLiveResidentChat();
  const dismissMutation = useDismissArgusLiveResidentChip();

  const { isStreaming, streamingText } = residentChat;

  // Auto-scroll to the latest message / pending indicator.
  useEffect(() => {
    tailRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  const chips: ResidentChip[] = chipsQuery.data?.chips ?? [];
  const proactiveEnabled = chipsQuery.data?.proactiveEnabled ?? false;

  /**
   * Chip click — pre-fill the composer (composer_query / open_approval) or
   * anchor a row, but NEVER auto-send and NEVER mint authz. The user always
   * reviews + presses Send. This is the COCKPIT-PLAN §3 "no-auth-on-click"
   * invariant.
   */
  const onChipClick = useCallback((chip: ResidentChip) => {
    if (chip.click.kind === 'composer_query') {
      setInput(chip.click.query);
    } else if (chip.click.kind === 'row_anchor') {
      setInput(
        `Tell me about row "${chip.click.rowKey}" in ${chip.click.tableSlug}.`,
      );
    } else if (chip.click.kind === 'open_approval') {
      // Surface the approval inline in the thread so the user can act on it.
      const { approvalId } = chip.click;
      setMessages((prev) => [
        ...prev,
        {
          id: makeResidentMsgId(),
          role: 'assistant',
          content: 'This item is waiting on your approval:',
          proposedActions: [
            {
              approvalId,
              actionId: chip.id,
              kind: 'mcp_tool',
              summary: chip.label,
            },
          ],
          timestamp: Date.now(),
        },
      ]);
    }
    composerRef.current?.focus();
  }, []);

  const onDismissChip = useCallback(
    (chip: ResidentChip) => {
      dismissMutation.mutate({ dashboardId, chipId: chip.id });
    },
    [dashboardId, dismissMutation],
  );

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput('');

    const userMsg: ResidentMessage = {
      id: makeResidentMsgId(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg]);

    try {
      // Streams tokens into `residentChat.streamingText` (rendered live as the
      // in-flight bubble below); resolves with the settled final payload.
      const res = await residentChat.send({
        dashboardId,
        message: text,
        ...(sessionId ? { sessionId } : {}),
      });
      setMessages((prev) => [
        ...prev,
        {
          id: makeResidentMsgId(),
          role: 'assistant',
          content: res.answer,
          proposedActions:
            res.proposedActions.length > 0 ? res.proposedActions : undefined,
          timestamp: Date.now(),
        },
      ]);
    } catch (err) {
      // Aborts are silent (the user navigated away / sent again).
      if ((err as Error)?.name === 'AbortError') return;
      setMessages((prev) => [
        ...prev,
        {
          id: makeResidentMsgId(),
          role: 'assistant',
          content: `_The resident couldn't answer: ${
            (err as Error).message
          }_`,
          timestamp: Date.now(),
        },
      ]);
    }
  }, [input, isStreaming, residentChat, dashboardId, sessionId]);

  const reset = useCallback(() => {
    setInput('');
    setMessages([
      {
        id: makeResidentMsgId(),
        role: 'assistant',
        content: RESIDENT_WELCOME,
        timestamp: Date.now(),
      },
    ]);
  }, []);

  return (
    <div
      className="flex flex-col h-full min-h-0 min-w-0 border-l border-border/40 bg-card/30"
      data-testid="cockpit-resident-panel"
    >
      {/* Header */}
      <div className="border-b border-border/40 px-3 py-2 flex items-center justify-between shrink-0 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <ArgusEye state="idle" className="h-5 w-5 text-primary shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold truncate">Resident</span>
            <span className="text-[10px] text-muted-foreground truncate">
              Ask about this cockpit
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={reset}
          title="Reset conversation"
          aria-label="Reset conversation"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Chips lane (≤3) */}
      {chips.length > 0 && (
        <div
          className="border-b border-border/40 px-3 py-2 space-y-1.5 shrink-0 bg-amber-500/[0.03]"
          data-testid="cockpit-resident-chips"
        >
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Bell className="h-3 w-3" />
            Suggestions
            {!proactiveEnabled && (
              <Badge
                variant="outline"
                className="text-[8px] ml-1 border-muted-foreground/30"
              >
                paused
              </Badge>
            )}
          </div>
          {chips.slice(0, 3).map((chip) => (
            <div
              key={chip.id}
              className="flex items-center gap-1 group"
              data-testid="cockpit-resident-chip"
            >
              <button
                type="button"
                onClick={() => onChipClick(chip)}
                className="flex-1 text-left rounded-md border border-border/60 bg-background hover:border-primary/40 hover:bg-accent/40 px-2.5 py-1.5 text-xs transition-colors min-w-0"
                title={chip.label}
                data-chip-kind={chip.kind}
              >
                <span className="truncate block">{chip.label}</span>
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 opacity-50 group-hover:opacity-100"
                onClick={() => onDismissChip(chip)}
                title="Dismiss suggestion"
                aria-label="Dismiss suggestion"
                disabled={dismissMutation.isPending}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div
        className="flex-1 overflow-auto p-3 space-y-3"
        data-testid="cockpit-resident-messages"
      >
        {messages.map((msg) => {
          if (msg.role === 'assistant') {
            return (
              <div key={msg.id} className="flex gap-2.5">
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <ArgusEye state="idle" className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 max-w-[88%] space-y-2">
                  <div className="bg-muted/40 rounded-2xl rounded-tl-sm px-4 py-3">
                    {msg.content ? (
                      <MarkdownRenderer content={msg.content} />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5 animate-pulse text-muted-foreground" />
                    )}
                  </div>
                  {/* Proposed (approval-gated) writes surface inline. */}
                  {msg.proposedActions?.map((pa) => (
                    <ApprovalCard
                      key={pa.approvalId || pa.actionId}
                      approvalId={pa.approvalId || undefined}
                      args={{ action: pa.actionId, kind: pa.kind }}
                      summary={pa.summary}
                    />
                  ))}
                </div>
              </div>
            );
          }
          return (
            <div key={msg.id} className="flex justify-end">
              <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm max-w-[85%]">
                {msg.content}
              </div>
            </div>
          );
        })}
        {isStreaming && (
          <div className="flex gap-2.5" data-testid="cockpit-resident-streaming">
            <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <ArgusEye
                state="thinking"
                blink="fast"
                className="h-5 w-5 text-primary"
              />
            </div>
            <div className="min-w-0 max-w-[88%]">
              <div className="bg-muted/40 rounded-2xl rounded-tl-sm px-4 py-3">
                {streamingText ? (
                  // Tokens render as they arrive (typing effect).
                  <MarkdownRenderer content={streamingText} />
                ) : (
                  // Pre-first-token: the thinking indicator (the resident
                  // deliberates in its hidden analysis channel first).
                  <div className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" />
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce"
                      style={{ animationDelay: '150ms' }}
                    />
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce"
                      style={{ animationDelay: '300ms' }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div ref={tailRef} />
      </div>

      {/* Composer */}
      <div className="border-t border-border/40 p-3 shrink-0">
        <div className="flex gap-2">
          <Textarea
            ref={composerRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder="Ask the resident…"
            className={cn('min-h-[40px] max-h-[120px] resize-none text-sm')}
            disabled={isStreaming}
            rows={1}
            aria-label="Resident message input"
            data-testid="cockpit-resident-input"
          />
          <Button
            onClick={() => void send()}
            disabled={isStreaming || !input.trim()}
            size="icon"
            className="h-10 w-10 shrink-0 rounded-xl"
            aria-label="Send message"
            title="Send (Enter)"
            data-testid="cockpit-resident-send-button"
          >
            {isStreaming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowUp className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
